// Shared UI components used across screens
import { useState } from 'react';
import { ChevronDown, ArrowLeft, Menu, X, Wrench, Calculator, Settings, Home } from 'lucide-react';
export { default as FeedbackWidget } from './FeedbackWidget';
export { default as ConfidenceBadge, getConfidenceColor } from './ConfidenceBadge';
export { default as LockFields } from './LockFields';
export { default as NotesSlideshow } from './NotesSlideshow';

export const colors = {
  yellow: '#E2FF2E',
  yellowDim: 'rgba(226, 255, 46, 0.15)',
  black: '#0A0A0A',
  cardBg: '#1A1A1A',
  inputBg: '#252525',
  textPrimary: '#FFFFFF',
  textSecondary: '#A0A0A0',
  textMuted: '#666666',
  green: '#4ADE80',
  greenDim: 'rgba(74, 222, 128, 0.15)',
  red: '#F87171',
  redDim: 'rgba(248, 113, 113, 0.15)',
  orange: '#FB923C',
  orangeDim: 'rgba(251, 146, 60, 0.15)',
  border: '#2A2A2A',
};

export const Header = ({ title, subtitle, showBack, onBack, rightAction }) => (
  <div className="sticky top-0 z-40 px-4 pt-6 pb-5 flex items-center justify-between" style={{ background: colors.black }}>
    <div className="flex items-center gap-3">
      {showBack && <button onClick={onBack} style={{ color: colors.yellow }}><ArrowLeft size={24} /></button>}
      <div>
        <h1 className="text-xl font-bold" style={{ color: colors.textPrimary }}>{title}</h1>
        {subtitle && <p className="text-sm" style={{ color: colors.textSecondary }}>{subtitle}</p>}
      </div>
    </div>
    {rightAction}
  </div>
);

export const PillToggle = ({ options, value, onChange }) => (
  <div className="flex gap-1 p-1 rounded-xl" style={{ background: colors.cardBg }}>
    {options.map(opt => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        className="flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all"
        style={{
          background: value === opt.value ? colors.yellow : 'transparent',
          color: value === opt.value ? colors.black : colors.textMuted
        }}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

export const SelectField = ({ label, value, onChange, options, placeholder, disabled }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>{label}</label>
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-4 py-3 rounded-xl appearance-none outline-none"
        style={{
          background: colors.inputBg,
          color: value ? colors.textPrimary : colors.textMuted,
          border: `1px solid ${colors.border}`,
          opacity: disabled ? 0.5 : 1
        }}
      >
        <option value="">{placeholder}</option>
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
      <ChevronDown size={18} className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: colors.textMuted }} />
    </div>
  </div>
);

export const LoadingSpinner = ({ size = 'md', color = colors.yellow }) => {
  const sizes = { sm: 'h-4 w-4', md: 'h-8 w-8', lg: 'h-12 w-12' };
  return (
    <div className={`animate-spin rounded-full ${sizes[size]} border-t-2 border-b-2`} style={{ borderColor: color }} />
  );
};

export const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-12">
    {Icon && <Icon size={48} className="mx-auto mb-4 opacity-30" style={{ color: colors.textMuted }} />}
    <p className="font-medium mb-1" style={{ color: colors.textSecondary }}>{title}</p>
    {description && <p className="text-sm" style={{ color: colors.textMuted }}>{description}</p>}
  </div>
);

export const StatusBadge = ({ status, children }) => {
  const styles = {
    open: { bg: colors.greenDim, color: colors.green },
    closed: { bg: colors.yellowDim, color: colors.yellow },
    pending: { bg: colors.yellowDim, color: colors.yellow },
    'in-progress': { bg: colors.orangeDim, color: colors.orange },
    complete: { bg: colors.greenDim, color: colors.green },
    urgent: { bg: colors.redDim, color: colors.red },
  };

  const style = styles[status] || styles.pending;

  return (
    <span
      className="px-2 py-1 rounded-full text-xs font-semibold"
      style={{ background: style.bg, color: style.color }}
    >
      {children || status}
    </span>
  );
};

export const Card = ({ children, className = '', style = {}, onClick }) => (
  <div
    className={`rounded-2xl p-4 ${className} ${onClick ? 'cursor-pointer active:scale-98 transition-transform' : ''}`}
    style={{ background: colors.cardBg, ...style }}
    onClick={onClick}
  >
    {children}
  </div>
);

export const Button = ({ children, variant = 'primary', disabled, onClick, className = '' }) => {
  const variants = {
    primary: { background: colors.yellow, color: colors.black },
    secondary: { background: colors.cardBg, color: colors.textSecondary, border: `1px solid ${colors.border}` },
    success: { background: colors.greenDim, color: colors.green },
    danger: { background: colors.redDim, color: colors.red },
  };

  const style = variants[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all active:scale-98 disabled:opacity-50 ${className}`}
      style={style}
    >
      {children}
    </button>
  );
};

// Top navigation bar for app-wide navigation
const NAV_ITEMS = [
  { href: '/', label: 'Home', icon: Home },
  { href: '/trades-app', label: 'Trades', icon: Wrench },
  { href: '/insurance-estimator', label: 'Estimator', icon: Calculator },
  { href: '/admin', label: 'Admin', icon: Settings },
];

export const TopNav = ({ currentPath = '/' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (href) => {
    if (href === '/') return currentPath === '/' || currentPath === '';
    return currentPath.startsWith(href);
  };

  return (
    <nav className="border-b" style={{ background: colors.black, borderColor: colors.border }}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: colors.yellow }}>
              <Wrench size={18} style={{ color: colors.black }} />
            </div>
            <span className="font-bold hidden sm:block" style={{ color: colors.textPrimary }}>
              PropSuite
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <a
                  key={item.href}
                  href={item.href}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{
                    background: active ? colors.yellowDim : 'transparent',
                    color: active ? colors.yellow : colors.textSecondary,
                  }}
                >
                  <Icon size={16} />
                  {item.label}
                </a>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg"
            style={{ color: colors.textSecondary }}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-3 border-t" style={{ borderColor: colors.border }}>
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <a
                  key={item.href}
                  href={item.href}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium"
                  style={{
                    background: active ? colors.yellowDim : 'transparent',
                    color: active ? colors.yellow : colors.textSecondary,
                  }}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Icon size={18} />
                  {item.label}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
};
